// firebase/useDrafts.ts
// Custom hook for draft management with auto-save

import { useState, useEffect, useCallback } from 'react';
import { collection, doc, setDoc, getDoc, getDocs, query, where, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './config';
import { useAuth } from '../contexts/AuthContext';
import { PostType, PostCategory } from '../types';

export interface DraftData {
    id: string;
    type: PostType;
    title: string;
    caption?: string;
    content?: string;
    category: PostCategory;
    videoFileName?: string;
    audioFileName?: string;
    thumbnailFileName?: string;
    authorEmail: string;
    lastSaved: Date;
    createdAt: Date;
}

export const useDrafts = (postType: PostType) => {
    const { currentUser } = useAuth();
    const [drafts, setDrafts] = useState<DraftData[]>([]);
    const [loading, setLoading] = useState(false);
    const [autoSaveStatus, setAutoSaveStatus] = useState<'saved' | 'saving' | 'idle'>('idle');

    // Load all drafts for current user and post type
    const loadDrafts = useCallback(async () => {
        if (!currentUser?.email) return;

        setLoading(true);
        try {
            const q = query(
                collection(db, 'barkBoard', 'drafts', 'items'),
                where('authorEmail', '==', currentUser.email),
                where('type', '==', postType)
            );

            const snapshot = await getDocs(q);
            const loadedDrafts = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                lastSaved: doc.data().lastSaved?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
            })) as DraftData[];

            setDrafts(loadedDrafts);
        } catch (err) {
            console.error('Failed to load drafts:', err);
        } finally {
            setLoading(false);
        }
    }, [currentUser?.email, postType]);

    // Save draft
    const saveDraft = useCallback(async (data: Partial<DraftData>, draftId?: string) => {
        if (!currentUser?.email) return null;

        setAutoSaveStatus('saving');
        try {
            const id = draftId || `draft_${postType}_${Date.now()}`;

            // Remove undefined fields
            const cleanData = Object.fromEntries(
                Object.entries(data).filter(([_, v]) => v !== undefined)
            );

            const draftData: any = {
                ...cleanData,
                id,
                type: postType,
                authorEmail: currentUser.email,
                lastSaved: serverTimestamp(),
            };

            if (!draftId) {
                draftData.createdAt = serverTimestamp();
            }

            await setDoc(
                doc(db, 'barkBoard', 'drafts', 'items', id),
                draftData,
                { merge: true }
            );

            setAutoSaveStatus('saved');
            setTimeout(() => setAutoSaveStatus('idle'), 2000);

            return id;
        } catch (err) {
            console.error('Failed to save draft:', err);
            setAutoSaveStatus('idle');
            return null;
        }
    }, [currentUser?.email, postType]);

    // Load specific draft
    const loadDraft = async (draftId: string): Promise<DraftData | null> => {
        try {
            const docRef = doc(db, 'barkBoard', 'drafts', 'items', draftId);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) return null;

            const data = docSnap.data();
            return {
                id: docSnap.id,
                ...data,
                lastSaved: data.lastSaved?.toDate(),
                createdAt: data.createdAt?.toDate(),
            } as DraftData;
        } catch (err) {
            console.error('Failed to load draft:', err);
            return null;
        }
    };

    // Delete draft
    const deleteDraft = async (draftId: string) => {
        try {
            await deleteDoc(doc(db, 'barkBoard', 'drafts', 'items', draftId));
            setDrafts(drafts.filter(d => d.id !== draftId));
        } catch (err) {
            console.error('Failed to delete draft:', err);
        }
    };

    useEffect(() => {
        loadDrafts();
    }, [loadDrafts]);

    return {
        drafts,
        loading,
        autoSaveStatus,
        saveDraft,
        loadDraft,
        deleteDraft,
        loadDrafts,
    };
};